import moment from 'moment-timezone';
import uPlot from 'uplot';
import { TooltipData, TooltipSeriesItem } from './types';

/** Pixel distance within which the cursor is considered to be over a specific line. */
export const LINE_PROXIMITY_THRESHOLD_PX = 12;

type ResolveTooltipParams = {
  timezone?: string;
  tooltipTimestampFormat: string;
  mouseX: number;
  mouseY: number;
  tooltipWidth?: number;
  tooltipHeight?: number;
};

/**
 * Reads a series label from uPlot config or DOM-backed legend labels.
 */
export function getSeriesLabel(label: string | HTMLElement): string {
  if (typeof label === 'string') {
    return label;
  }
  return label.textContent || 'Unknown';
}

/**
 * Builds a plain-text representation of tooltip data for alert dialogs.
 */
export function formatTooltipAlertText(data: TooltipData): string {
  if (data.mode === 'single' && data.series.length === 1) {
    const item = data.series[0];
    return `${getSeriesLabel(item.label)}\n${data.timestamp}\n${item.value.toLocaleString()}`;
  }

  const lines = [
    data.timestamp,
    ...data.series.map((item) => `${getSeriesLabel(item.label)}: ${item.value.toLocaleString()}`),
  ];
  return lines.join('\n');
}

/**
 * Resolves tooltip payload for a cursor position over the chart plotting area.
 * Returns null when the point is outside bounds or has no visible series values.
 */
export function resolveTooltipAtPoint(
  uplot: uPlot,
  params: ResolveTooltipParams,
): TooltipData | null {
  const uOverDiv = uplot.root.querySelector('.u-over') as HTMLElement | null;
  if (!uOverDiv) {
    return null;
  }

  const rect = uOverDiv.getBoundingClientRect();
  const { mouseX, mouseY, timezone, tooltipTimestampFormat } = params;

  const scaleY = uplot.bbox.height / rect.height;
  const scaledMouseY = mouseY * scaleY + uplot.bbox.top;

  if (mouseX < 0 || mouseX > uplot.bbox.width || mouseY < 0 || mouseY > uplot.bbox.height) {
    return null;
  }

  const idx = uplot.posToIdx(mouseX);
  if (idx === null || idx < 0) {
    return null;
  }

  const timestampValue = uplot.data[0][idx];
  const series: TooltipSeriesItem[] = [];
  let closestSeriesIndex = -1;
  let minDistance = Infinity;

  for (let i = 1; i < uplot.data.length; i++) {
    const value = uplot.data[i][idx];
    const seriesConfig = uplot.series[i];

    if (value !== undefined && value !== null && seriesConfig?.show !== false) {
      const seriesY = uplot.valToPos(value, 'y', true);
      const distance = Math.abs(scaledMouseY - seriesY);

      if (distance < minDistance) {
        minDistance = distance;
        closestSeriesIndex = i;
      }

      series.push({
        uPlotSeriesIndex: i,
        label: seriesConfig?.label || `Series ${i}`,
        value,
        color:
          typeof seriesConfig?.stroke === 'function'
            ? seriesConfig.stroke(uplot, i)
            : seriesConfig?.stroke || '#000',
      });
    }
  }

  if (series.length === 0) {
    return null;
  }

  const isSingleLine = minDistance < LINE_PROXIMITY_THRESHOLD_PX && closestSeriesIndex > 0;
  const displaySeries = isSingleLine
    ? series.filter((item) => item.uPlotSeriesIndex === closestSeriesIndex)
    : [...series].sort((a, b) => b.value - a.value);

  const padding = 10;
  const tooltipWidth = params.tooltipWidth ?? 200;
  const tooltipHeight = params.tooltipHeight ?? 100;

  let tooltipX = mouseX + 100;
  if (tooltipX + tooltipWidth + padding > rect.width) {
    tooltipX = mouseX - tooltipWidth - 15;
  }
  if (tooltipX < padding) {
    tooltipX = padding;
  }

  let tooltipY = mouseY + 40;
  if (tooltipY + tooltipHeight + padding > rect.height) {
    tooltipY = mouseY - tooltipHeight - 15;
  }
  if (tooltipY < padding) {
    tooltipY = padding;
  }

  return {
    mode: isSingleLine ? 'single' : 'all',
    seriesIndex: isSingleLine ? closestSeriesIndex : undefined,
    timestampUnix: timestampValue,
    series: displaySeries,
    x: tooltipX,
    y: tooltipY,
    timestamp: timezone
      ? moment.unix(timestampValue).tz(timezone).format(tooltipTimestampFormat)
      : new Date(timestampValue * 1000).toLocaleString(),
  };
}

/**
 * Returns true when the event target is an interactive or non-plot chart region.
 */
export function isChartExcludedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest('.u-legend') ||
    target.closest('.u-legend tbody') ||
    target.closest('[class*="legend"]') ||
    target.closest('[class*="control"]') ||
    target.closest('button') ||
    target.closest('[class*="axis"]') ||
    target.closest('[role="button"]'),
  );
}
